/**
 * LazyMount — mount `children` only while the slot is on (or near) the screen.
 *
 * The primitive behind "hundreds of embedded boards per page": each board is
 * expensive (a projected subspace, a live DOM board, its observers), so a
 * catalog that mounts all of them pays O(total). Wrapping each in a `LazyMount`
 * makes the cost O(visible): the slot always renders as a cheap sized
 * placeholder (reserving its box so scrolling never reflows), and the real
 * board mounts only when the slot scrolls within `overscanPx` of the viewport,
 * unmounting again when it leaves (unless `keepMounted`). Backed by the shared
 * `IntersectionObserver`, so N slots cost one native observer, not N.
 *
 * Layout-agnostic by design — it observes its own box, so boards can sit in a
 * grid, a flex row, or be interleaved with arbitrary page content, unlike a
 * windowing list that must own a uniform scroller.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { observeIntersection } from "../board/sharedIntersectionObserver.js";

export interface LazyMountProps {
  /** Placeholder box style — MUST reserve the slot's final size so mounting the
   *  real content doesn't shift scroll position. */
  readonly style?: CSSProperties;
  readonly className?: string;
  /** Pixels beyond the viewport edge to pre-mount, so a board is ready before
   *  it scrolls fully into view. Default 300. */
  readonly overscanPx?: number;
  /** Keep children mounted once first seen (skip the unmount-on-leave reclaim).
   *  Trades memory for no re-solve flicker when scrolling back. Default false. */
  readonly keepMounted?: boolean;
  /** Rendered in the slot while the real content is unmounted. */
  readonly placeholder?: ReactNode;
  readonly children: ReactNode;
}

export function LazyMount({
  style,
  className,
  overscanPx = 300,
  keepMounted = false,
  placeholder,
  children,
}: LazyMountProps): ReactNode {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // No IntersectionObserver (SSR / jsdom without a polyfill): degrade to
    // eager mount so content is never blank.
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const dispose = observeIntersection(
      el,
      (entry) => {
        if (entry.isIntersecting) setVisible(true);
        else if (!keepMounted) setVisible(false);
      },
      { rootMargin: `${overscanPx}px` },
    );
    return dispose;
  }, [overscanPx, keepMounted]);

  return (
    <div ref={ref} className={className} style={style} data-lazy-mounted={visible ? "true" : "false"}>
      {visible ? children : placeholder ?? null}
    </div>
  );
}
